/**
 * ScoreImageUpload — drag-drop a photo of marked student work, AI extracts
 * scores, teacher reviews + confirms before save.
 *
 * Flow (matches B3 backend):
 *   1. Teacher picks an image file (PNG/JPEG)
 *   2. (Optional) adds a custom_prompt with extra context
 *   3. Clicks "上传识别" → POST multipart to /assignments/{id}/scores/from-image
 *   4. Backend calls Claude vision, returns VisionCandidate[]
 *   5. Component renders an editable preview grid pre-populated with the
 *      extracted scores. Teacher can correct, then click "保存全部"
 *   6. Saves via the existing useSubmitScores mutation (same path as
 *      manual entry — image-upload is "another way to populate the grid")
 *
 * Mock-mode (no MEGANOVA_API_KEY) shows a yellow banner so the operator
 * knows the candidates are synthetic.
 */
import { useEffect, useState } from "react";

import {
  uploadScoreImage,
  useSubmitScores,
  type Student,
  type VisionCandidate,
} from "../api/school";
import { CustomPromptInput } from "./CustomPromptInput";

interface ScoreImageUploadProps {
  assignmentId: string;
  students: Student[];
  maxScore: number;
  onSaved?: () => void;
}

export function ScoreImageUpload({
  assignmentId,
  students,
  maxScore,
  onSaved,
}: ScoreImageUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [phase, setPhase] = useState<"pick" | "uploading" | "review" | "saved">(
    "pick",
  );
  const [candidates, setCandidates] = useState<VisionCandidate[]>([]);
  const [mockMode, setMockMode] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const submit = useSubmitScores(assignmentId);

  // Build editable shadow state whenever candidates change.
  useEffect(() => {
    setEdits(
      Object.fromEntries(
        candidates.map((c) => [c.student_id, c.score !== null ? String(c.score) : ""]),
      ),
    );
  }, [candidates]);

  async function handleUpload() {
    if (files.length === 0) return;
    setErr(null);
    setPhase("uploading");
    try {
      // 多张按序识别,按学生合并(后一张的结果覆盖同一学生,支持
      // 「一沓卷子分几张拍」的现场用法)。
      const merged = new Map<string, VisionCandidate>();
      let mock = false;
      for (let i = 0; i < files.length; i++) {
        setProgress(files.length > 1 ? `识别第 ${i + 1}/${files.length} 张…` : "");
        const resp = await uploadScoreImage(assignmentId, files[i]!, customPrompt.trim() || undefined);
        mock = mock || resp.mock_mode;
        for (const c of resp.candidates) merged.set(c.student_id, c);
      }
      setCandidates([...merged.values()]);
      setMockMode(mock);
      setPhase("review");
    } catch (e) {
      setErr((e as Error).message);
      setPhase("pick");
    } finally {
      setProgress("");
    }
  }

  async function handleSaveAll() {
    setErr(null);
    try {
      const rows = candidates
        .map((c) => {
          const raw = edits[c.student_id]?.trim() ?? "";
          if (raw === "") return null;
          const num = Number(raw);
          if (Number.isNaN(num)) throw new Error(`无效分数：${raw}`);
          if (num < 0 || num > maxScore)
            throw new Error(`分数 ${num} 超出 0–${maxScore}`);
          return { student_id: c.student_id, score: num };
        })
        .filter((r): r is { student_id: string; score: number } => r !== null);
      if (rows.length === 0) throw new Error("没有可保存的分数");
      await submit.mutateAsync(rows);
      setPhase("saved");
      onSaved?.();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const nameById = new Map(students.map((s) => [s.id, s]));

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-2">
        📷 拍照识别成绩（AI 提取）
      </h4>

      {/* Phase 1: pick image */}
      {phase === "pick" && (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <input
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              className="block text-sm"
            />
            {/* 移动端直接调后置摄像头(TD-5):capture 单独一个入口,
                不影响桌面/相册选择。拍一张追加一张。 */}
            <label className="cursor-pointer rounded border border-sky-300 bg-white px-3 py-1.5 text-sm text-sky-700 sm:hidden">
              📷 拍照
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setFiles((prev) => [...prev, f]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {files.length > 0 && (
            <div className="text-xs text-slate-500 mb-2">
              已选择 {files.length} 张：
              {files.map((f) => (
                <span key={f.name + f.size} className="font-mono mr-2">
                  {f.name}({(f.size / 1024).toFixed(0)}KB)
                </span>
              ))}
            </div>
          )}
          <div className="mb-2">
            <CustomPromptInput
              value={customPrompt}
              onChange={setCustomPrompt}
              label="额外指示（可选）"
              placeholder="例如：分数模糊请标记 uncertain；如果姓名不清晰请仅返回 student_id"
              surface="assignment"
              context={{ max_score: maxScore, roster_size: students.length }}
            />
          </div>
          {err && <div className="text-xs text-red-600 mb-2">{err}</div>}
          <button
            type="button"
            onClick={handleUpload}
            disabled={files.length === 0}
            className="px-3 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
          >
            上传识别
          </button>
        </>
      )}

      {/* Phase 2: uploading spinner */}
      {phase === "uploading" && (
        <div className="text-sm text-slate-600 italic">
          AI 正在识别图片中的成绩… (~3 秒/张){progress && ` · ${progress}`}
        </div>
      )}

      {/* Phase 3: review + edit */}
      {phase === "review" && (
        <>
          {mockMode && (
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
              ⚠️ 演示模式：未配置 MEGANOVA_API_KEY，返回的是模拟数据。
            </div>
          )}
          <div className="text-xs text-slate-500 mb-2">
            AI 识别到 {candidates.length} 个学生分数。请核对后保存。
          </div>
          {(() => {
            const got = new Set(candidates.map((c) => c.student_id));
            const missing = students.filter((s2) => !got.has(s2.id));
            if (missing.length === 0) return null;
            return (
              <div className="mb-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
                ⚠️ {missing.length} 名学生未识别到分数：
                {missing.slice(0, 10).map((s2) => s2.name_zh).join("、")}
                {missing.length > 10 ? " 等" : ""}。
                可能是没拍到或字迹模糊 —— 请对这部分补拍重传（结果会合并），或切回手动录入补填。
              </div>
            );
          })()}
          <div className="rounded border border-slate-200 bg-white overflow-x-auto mb-3">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">学生</th>
                  <th className="text-left px-3 py-2">分数 / {maxScore}</th>
                  <th className="text-left px-3 py-2">AI 备注</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const s = nameById.get(c.student_id);
                  return (
                    <tr key={c.student_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-800">
                        {s ? s.name_zh : c.student_id.slice(0, 8)}
                        {s?.name_en && (
                          <span className="text-slate-400 text-xs ml-1">({s.name_en})</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min={0}
                          max={maxScore}
                          step="0.1"
                          className={`w-24 px-2 py-1 rounded border ${
                            c.uncertain
                              ? "border-amber-400 bg-amber-50"
                              : "border-slate-200"
                          }`}
                          value={edits[c.student_id] ?? ""}
                          onChange={(e) =>
                            setEdits((prev) => ({ ...prev, [c.student_id]: e.target.value }))
                          }
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {c.uncertain && <span className="text-amber-700 font-bold mr-1">⚠</span>}
                        {c.notes || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {err && <div className="text-xs text-red-600 mb-2">{err}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setPhase("pick");
                setFiles([]);
                setCandidates([]);
              }}
              className="px-3 py-1.5 rounded border border-slate-200 bg-white text-sm hover:bg-slate-50"
            >
              重新上传
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={submit.isPending}
              className="px-3 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
            >
              {submit.isPending ? "保存中…" : "保存全部"}
            </button>
          </div>
        </>
      )}

      {/* Phase 4: saved */}
      {phase === "saved" && (
        <div className="text-sm text-emerald-700">
          ✅ 已保存 · 学生雷达图将在刷新后更新。
          <button
            type="button"
            onClick={() => {
              setPhase("pick");
              setFiles([]);
              setCandidates([]);
            }}
            className="ml-3 text-xs text-sky-600 hover:underline"
          >
            再上传一张
          </button>
        </div>
      )}
    </div>
  );
}
